import React, { useState, Fragment } from "react";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { UtilityFunctions } from "../../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../../utils/constants/stringConstants";
import { Helmet } from 'react-helmet';
import { userService, cookieService } from "../../_services";

// Define props type including router props
interface ARAffSignupProps extends RouteComponentProps<any> {
  nextButton: () => void;
}

const ARAffSignup: React.FC<ARAffSignupProps> = (props) => {
  // --- State Management ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [affiliateCode, setAffiliateCode] = useState(localStorage.getItem("referralCode") || '');
  const [pwdType, setPwdType] = useState("password");
  const [btnClick, setBtnClick] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState('');
  const [showVideoPlayer, setShowVideoPlayer] = useState(false); // Added for video player

  // --- Validation ---
  const hasNumber = /\d/;
  const isEmailValid = UtilityFunctions.validateEmail(email);
  const isPasswordValid = password.length >= 8 && hasNumber.test(password) && !UtilityFunctions.checkSpaceInPassword(password);
  const isMobileValid = !UtilityFunctions.isEmptyOrNullOrUndefined(mobileNumber);

  // Overall form validation
  const isFormValid = isEmailValid && isPasswordValid && isMobileValid;

  // --- Handlers ---
  const togglePassword = () => {
    setPwdType(pwdType === "password" ? "text" : "password");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBtnClick(true);
    setApiError(null);
  
    if (!isFormValid) {
      console.log("Validation failed, not submitting.");
      return;
    }
  
    setIsLoading(true);
    try {
      console.log("Attempting AR Signup with:", { email, password, mobileNumber, affiliateCode });
      
      const signupData = {
        email,
        password,
        mobileNumber,
        type: 'Mploi-er',
      };
      
      const response = await userService.signup(signupData, affiliateCode, true);
      
      const loginData = {
        email: email,
        password: password
      };
      
      const loginResponse = await userService.login(loginData);
      
      if (loginResponse.data && loginResponse.data.user && loginResponse.data.user.token) {
        localStorage.setItem('access_token', loginResponse.data.user.token.access);
        localStorage.setItem('refresh_token', loginResponse.data.user.token.refresh);
        localStorage.setItem('user_data', JSON.stringify(loginResponse.data.user));
        localStorage.setItem('isGuest', 'false');
        
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, loginResponse.data.user.token.access);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, loginResponse.data.user);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.isGuest, false);

        await userService.getUserData();
 
        props.history.push('/ar');
      }

    } catch (error: any) { // Added 'any' type for error for now, consider more specific typing
      console.error("API Call Failed:", error);
      
      if (error && Array.isArray(error) && error[0] === 'Email') {
        setApiError("Email already exists");
      } else {
        setApiError(
          error.response?.data?.message || 
          error.message || 
          "An unexpected error occurred during signup."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAffiliateSignup = () => {
    props.history.push('/accounts/affiliate');
  };

  const handlePlayVideo = () => {
    setShowVideoPlayer(true);
  };

  // --- Navigation Data ---
  const terms = { pathname: `/terms`, state: {} };
  const privacy = { pathname: `/privacy`, state: {} };
  const onBoarding = { pathname: "/", state: { activeIndex: 2 } };

  // --- Styles ---
  // Styles object remains the same as in your provided code
  const styles: { [key: string]: React.CSSProperties } = { // Added index signature for styles
    container: {
      fontFamily: "'DM Sans', sans-serif",
      backgroundColor: "#1a1a2e",
      minHeight: "100vh",
      color: "#ffffff",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "25px 50px",
    },
    logo: {
      cursor: "pointer",
      height: "40px",
    },
    socialIcons: {
      display: "flex",
    },
    socialIcon: {
      borderRadius: "50%",
      padding: "6px",
      height: "31px",
      width: "31px",
      background: "rgba(255, 255, 255, 0.2)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      textAlign: "center",
      margin: "0 5px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    divider: {
      opacity: 0.44,
      width: "100%",
      background: "linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0) 100%)",
      height: "1px",
      margin: "10px 0 30px",
    },
    mainContent: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 20px",
    },
    heroSection: {
      textAlign: "center",
      marginBottom: "50px",
    },
    title: {
      fontFamily: "'Urbanist', sans-serif",
      fontWeight: 700,
      fontSize: "48px",
      lineHeight: 1.2,
      margin: "44px 0 30px",
    },
    subtitle: {
      fontFamily: "'Urbanist', sans-serif",
      fontSize: "20px",
      color: "#fff",
      marginTop: "20px",
    },
    description: {
      fontSize: "16px",
      lineHeight: 1.5,
      color: "rgba(255, 255, 255, 0.8)",
      margin: "15px auto",
      maxWidth: "560px",
    },
    formSection: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginBottom: "60px",
    },
    formContainer: {
      background: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      padding: "40px",
      width: "100%",
      maxWidth: "500px",
      margin: "0 auto",
      border: "1px solid rgba(6, 239, 197, 0.2)",
    },
    formTitle: {
      color: "#06EFC5",
      fontSize: "24px",
      fontWeight: 700,
      marginBottom: "20px",
      textAlign: "center",
    },
    formGroup: {
      marginBottom: "20px",
      position: "relative",
    },
    input: {
      width: "100%",
      padding: "15px 45px 15px 15px",
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      border: "1px solid rgba(6, 239, 197, 0.3)",
      borderRadius: "8px",
      color: "#ffffff",
      fontSize: "16px",
      outline: "none",
      transition: "border-color 0.3s ease",
    },
    inputError: {
      borderColor: "#ff4757",
    },
    inputIcon: {
      position: "absolute",
      right: "15px",
      top: "50%",
      transform: "translateY(-50%)",
      cursor: "pointer",
    },
    validationList: {
      listStyle: "none",
      padding: "0",
      margin: "15px 0",
    },
    validationItem: {
      display: "flex",
      alignItems: "center",
      marginBottom: "8px",
      fontSize: "14px",
      color: "rgba(255, 255, 255, 0.7)",
    },
    validSuccess: {
      color: "#06EFC5",
    },
    validError: {
      color: "#ff4757",
    },
    button: {
      background: "#06EFC5",
      borderRadius: "8px",
      border: "none",
      boxShadow: "0px 10px 20px -10px rgba(6, 239, 197, 0.5)",
      padding: "16px 32px",
      fontFamily: "'DM Sans', sans-serif",
      fontWeight: 700,
      fontSize: "18px",
      color: "#1a1a2e",
      textDecoration: "none",
      display: "inline-block",
      margin: "20px 0",
      transition: "all 0.3s ease",
      cursor: "pointer",
      width: "100%",
    },
    buttonDisabled: {
      opacity: 0.7,
      cursor: "not-allowed",
    },
    videoSection: {
      marginBottom: "60px",
    },
    featuresSection: {
      marginBottom: "60px",
    },
    footer: {
      background: "#100027",
      padding: "40px 30px 60px",
      textAlign: "center",
    },
    accountButton: {
      backgroundColor: 'rgba(6, 239, 197, 0.1)',
      border: '1px solid #06EFC5',
      color: '#06EFC5',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      height: '35px',
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: '10px',
    },
    firstAccountButton: {
      backgroundColor: 'rgba(6, 239, 197, 0.1)',
      border: '1px solid #06EFC5',
      color: '#06EFC5',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      height: '35px',
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
  };

  return (
    <Fragment>
      <Helmet>
        <title>MPLOI: Boost Your Cash Flow with AI-Powered Invoice Collection</title>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Urbanist:wght@400;500;700&display=swap" rel="stylesheet" />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </Helmet>
      
      <div style={styles.container}>
        {/* Header */}
        <header style={styles.header}>
          <img 
            src={UtilityFunctions.getImageURL() + "logo_White.svg"} 
            alt="MPLOI Logo" 
            style={styles.logo}
            onClick={() => !isLoading && props.history.push(onBoarding)}
          />
          
          <div style={styles.socialIcons}>
            <a href="https://www.facebook.com/MPLOI.PLATFORM" target="_blank" rel="noopener noreferrer" style={styles.socialIcon}>
              <img src="https://mploi-public-bucket.s3.ap-southeast-2.amazonaws.com/facebook.png" alt="Facebook" />
            </a>
            <a href="https://www.instagram.com/accounts/login/?next=/mploi_app/" target="_blank" rel="noopener noreferrer" style={styles.socialIcon}>
              <img src="https://mploi-public-bucket.s3.ap-southeast-2.amazonaws.com/instagram.png" alt="Instagram" />
            </a>
            <a href="https://www.linkedin.com/in/brendon-clark-mploi" target="_blank" rel="noopener noreferrer" style={styles.socialIcon}>
              <img src="https://mploi-public-bucket.s3.ap-southeast-2.amazonaws.com/linked-in.png" alt="LinkedIn" />
            </a>
            <a href="https://www.tiktok.com/@mploi_app" target="_blank" rel="noopener noreferrer" style={styles.socialIcon}>
              <img src="https://mploi-public-bucket.s3.ap-southeast-2.amazonaws.com/ticktok.png" alt="TikTok" />
            </a>
          </div>
        </header>
        
        <div style={styles.divider}></div>
        
        <main style={styles.mainContent}>
          <section style={styles.heroSection}>
            <h1 style={styles.title}>Boost Your Cash Flow with AI-Powered Invoice Collection</h1>
            <p style={styles.subtitle}>Let MPLOI's AI handle your accounts receivable while you focus on growing your business</p>
            <p style={styles.description}>
              Our intelligent AI system calls your customers with outstanding invoices, maintaining a professional tone, and facilitating immediate payments - all without the awkward conversations.
            </p>
          </section>
          
          <section style={styles.formSection}>
            <div style={styles.formContainer}>
              <h2 style={styles.formTitle}>Create Your MPLOI AR Account</h2>
              
              <form onSubmit={handleSubmit} noValidate>
                <div style={styles.formGroup}>
                  <input
                    type="email"
                    placeholder="Email Address"
                    style={{...styles.input, ...(btnClick && !isEmailValid ? styles.inputError : {})}}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setBtnClick(false); setApiError(null); }}
                    disabled={isLoading}
                    required
                  />
                  <div style={styles.inputIcon}>
                    <img src={UtilityFunctions.getImageURL() + "mail.svg"} alt="" />
                  </div>
                  {btnClick && !isEmailValid && 
                    <div style={{ color: '#ff4757', fontSize: '12px', marginTop: '5px' }}>
                      Please enter a valid email address
                    </div>
                  }
                </div>

                <div style={styles.formGroup}>
                  <input
                    type="tel"
                    placeholder="Mobile Phone"
                    style={{...styles.input, ...(btnClick && !isMobileValid ? styles.inputError : {})}}
                    value={mobileNumber}
                    onChange={(e) => { setMobileNumber(e.target.value); setBtnClick(false); setApiError(null); }}
                    disabled={isLoading}
                    required
                  />
                  <div style={styles.inputIcon}>
                    <img src={UtilityFunctions.getImageURL() + "phone.svg"} alt="" />
                  </div>
                  {btnClick && !isMobileValid && 
                    <div style={{ color: '#ff4757', fontSize: '12px', marginTop: '5px' }}>
                      Please enter your mobile number
                    </div>
                  }
                </div>

                <div style={styles.formGroup}>
                  <input
                    type={pwdType}
                    placeholder="Password"
                    style={{...styles.input, ...(btnClick && !isPasswordValid ? styles.inputError : {})}}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setBtnClick(false); setApiError(null); }}
                    disabled={isLoading}
                    required
                  />
                  <div 
                    style={{...styles.inputIcon, cursor: 'pointer'}} 
                    onClick={togglePassword}
                  >
                    <img 
                      src={pwdType === "password" ? 
                        UtilityFunctions.getImageURL() + 'eyeOff.png' : 
                        UtilityFunctions.getImageURL() + 'eyeOn.png'} 
                      alt="Toggle password visibility" 
                    />
                  </div>
                  {btnClick && !isPasswordValid && 
                    <div style={{ color: '#ff4757', fontSize: '12px', marginTop: '5px' }}>
                      Password must meet the requirements below
                    </div>
                  }
                </div>
                
                <div style={styles.formGroup}>
                  <input
                    type="text"
                    placeholder="Affiliate Code (Optional)"
                    style={styles.input}
                    value={affiliateCode}
                    onChange={(e) => { setAffiliateCode(e.target.value); }}
                    disabled={isLoading || !!localStorage.getItem("referralCode")}
                  />
                  <div style={styles.inputIcon}>
                    <img src={UtilityFunctions.getImageURL() + "tag.svg"} alt="" style={{opacity: 0.5}} />
                  </div>
                </div>
                
                <ul style={styles.validationList}>
                  <li style={{
                    ...styles.validationItem,
                    ...(password.length <= 0 ? {} : password.length >= 8 && !UtilityFunctions.checkSpaceInPassword(password) ? styles.validSuccess : styles.validError)
                  }}>
                    ✓ {STRING_CONSTANTS.validationString.min_chars}
                  </li>
                  <li style={{
                    ...styles.validationItem,
                    ...(password.length <= 0 ? {} : hasNumber.test(password) ? styles.validSuccess : styles.validError)
                  }}>
                    ✓ {STRING_CONSTANTS.validationString.number_req}
                  </li>
                </ul>
                
                {apiError && (
                  <div style={{ color: "#ff4757", marginBottom: "15px", padding: "10px", backgroundColor: "rgba(255, 71, 87, 0.1)", borderRadius: "4px" }}>
                    {apiError}
                  </div>
                )}
                
                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...((!isFormValid || isLoading) ? styles.buttonDisabled : {})
                  }}
                  disabled={!isFormValid || isLoading}
                >
                  {isLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ 
                        width: '20px', 
                        height: '20px', 
                        border: '3px solid rgba(255, 255, 255, 0.3)', 
                        borderTop: '3px solid #1a1a2e', 
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite', // Ensure @keyframes spin is defined (added in Helmet)
                        marginRight: '10px'
                      }}></div>
                      Creating Account...
                    </div>
                  ) : "UNLOCK BETTER CASH FLOW NOW"}
                </button>
                
                <p style={{ fontSize: "14px", textAlign: "center", color: "rgba(255, 255, 255, 0.7)" }}>
                  By signing up, you agree to our{" "}
                  <Link to={terms} style={{ color: "#06EFC5" }}>Terms of Service</Link>{" "}
                  and{" "}
                  <Link to={privacy} style={{ color: "#06EFC5" }}>Privacy Policy</Link>
                </p>
                
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  <p style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Already have an account?{" "}
                    <Link to={STRING_CONSTANTS.navigationString.login} style={{ color: "#06EFC5" }}>
                      Sign In
                    </Link>
                  </p>
                  <p style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Want to become an affiliate?{" "}
                    <span onClick={handleAffiliateSignup} style={{ color: "#06EFC5", cursor: "pointer" }}>
                      Join our Affiliate Program
                    </span>
                  </p>
                </div>
              </form>
            </div>
          </section>
          
          {/* Video Section - MODIFIED */}
          <section style={styles.videoSection}>
            <h2 style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: "32px", textAlign: "center", marginBottom: "30px" }}>
              See How It Works
            </h2>
            <p style={styles.description}>
              Watch our 2-minute demo to learn how MPLOI AR helps businesses recover outstanding invoices automatically.
            </p>
            
            <div style={{
              background: "rgba(0, 0, 0, 0.3)",
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid rgba(6, 239, 197, 0.3)",
              position: "relative",
              maxWidth: "800px",
              margin: "0 auto",
            }}>
              {!showVideoPlayer ? (
                <>
                  <img 
                    src="https://res.cloudinary.com/dimqqmfx6/image/upload/v1743453131/placeholderm_gp8jmg.png" 
                    alt="MPLOI AR Demo Video Thumbnail - Click to play" 
                    style={{ width: "100%", display: "block", opacity: 0.8, cursor: 'pointer' }}
                    onClick={handlePlayVideo} // Allow clicking image itself too
                  />
                  <div 
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      background: "rgba(6, 239, 197, 0.8)",
                      borderRadius: "50%",
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    onClick={handlePlayVideo}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePlayVideo(); }}
                    aria-label="Play video"
                  >
                    <div style={{
                      width: "0",
                      height: "0",
                      borderTop: "15px solid transparent",
                      borderBottom: "15px solid transparent",
                      borderLeft: "25px solid #1a1a2e",
                      marginLeft: "5px",
                    }}></div>
                  </div>
                </>
              ) : (
                <div style={{ position: 'relative', paddingTop: '56.25%' /* 16:9 Aspect Ratio */, backgroundColor: '#000' }}> 
                  <iframe
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    src="https://www.youtube.com/embed/psdciFVMNfY?autoplay=1&rel=0&modestbranding=1&showinfo=0"
                    title="MPLOI AR Demo Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          </section>
          
          <section style={styles.featuresSection}>
            <h2 style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: "32px", textAlign: "center", marginBottom: "30px" }}>
              Why Choose MPLOI Accounts Receivable?
            </h2>
            
            <div style={{
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "12px",
              padding: "30px",
              border: "1px solid rgba(6, 239, 197, 0.2)",
              marginBottom: "40px",
            }}>
              {[
                {
                  title: "Your AI-Powered Invoice Follow-ups",
                  text: "No more uncomfortable conversations asking customers to pay outstanding invoices. Let our AI handle it while you focus on running your business."
                },
                {
                  title: "Seamless Software Integration",
                  text: "MPLOI integrates with all your favorite accounting software including Xero and QuickBooks, or simply upload an Excel spreadsheet of your outstanding debtors."
                },
                {
                  title: "Customizable AI Team Member",
                  text: "Choose from multiple voices and tonalities for your AI Team Member, ensuring the best possible customer experience while collecting payments."
                },
                {
                  title: "Personalized Mobile Number",
                  text: "Get a dedicated mobile number for your business where customers can call back regarding their invoices."
                },
                {
                  title: "Complete Call Tracking",
                  text: "Every call is transcribed, logged, and emailed directly to you so you can see the outcome of each interaction."
                },
                {
                  title: "Smart Follow-ups",
                  text: "Your AI assistant remembers previous conversations with debtors and follows up appropriately, maintaining context across multiple interactions."
                }
              ].map((benefit, index) => (
                <div key={index} style={{ display: "flex", alignItems: "flex-start", marginBottom: "15px" }}>
                  <div style={{ color: "#06EFC5", marginRight: "15px", fontSize: "20px", fontWeight: "bold" }}>✓</div>
                  <div style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "16px", lineHeight: "1.5" }}>
                    <strong>{benefit.title}:</strong> {benefit.text}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>
        
        <footer style={styles.footer}>
          <p style={{ color: "#ffffff", fontSize: "16px" }}>
            Questions? Contact our support team
          </p>
          <a href="mailto:support@mploi.com" style={{ color: "#06EFC5", textDecoration: "none" }}>
            support@mploi.com
          </a>
          
          <div style={{ margin: "20px 0" }}>
            <Link to={privacy} style={{ color: "#06EFC5", textDecoration: "none", margin: "0 10px", fontSize: "14px" }}>
              Privacy Policy
            </Link> | 
            <Link to={terms} style={{ color: "#06EFC5", textDecoration: "none", margin: "0 10px", fontSize: "14px" }}>
              Terms & Conditions
            </Link>
          </div>
          
          <p style={{ marginTop: "30px", fontSize: "12px", color: "#7d7d7d" }}>
            All rights reserved. Copyright © {new Date().getFullYear()} MPLOI
          </p>
        </footer>
      </div>
    </Fragment>
  );
};

export default withRouter(ARAffSignup);